import { useNeedsSupportSelection } from 'hooks/useNeedsSupportSelection';
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import classNames from 'classnames';
import { useIsMobile } from 'hooks/useIsMobile';
import {
  useSessionIdentity,
  useSessionVerification,
} from 'hooks/useSessionVerification';
import { shouldApplyBullhornEmbedZoom } from 'utils/bullhornEmbed';
import { lazyWithReload } from 'utils/lazyWithReload';
import { FeatureErrorBoundary } from 'components/FeatureErrorBoundary';
import SideNavBar from 'components/side-nav-bar/SideNavBar';
import { NoPhoneNumbersBanner } from 'components/org/NoPhoneNumbersBanner';
import { SmsRegistrationDeadlineBanner } from 'components/org/SmsRegistrationDeadlineBanner';
import BullhornEmbedNav from 'components/bullhorn/BullhornEmbedNav';
import { WhatsNewPopup } from 'components/whats-new/WhatsNewPopup';
import { FeatureFlag, useFeatureFlagsStore } from 'stores/featureFlagsStore';
import { useAppNavigate } from 'url-registry';

import { isProtectedPagePath } from './preloadRouteChunk';
import appShell from '../App.module.css';
import { shouldHideSideNavBar, shouldUseContentsDisplay } from './hideNav';
import { useBullhornEmbedMode } from './hooks/useBullhornEmbedMode';
import { useCrossTabAuthSync } from './hooks/useCrossTabAuthSync';
import { useDatadogRumUser } from './hooks/useDatadogRumUser';
import { useFeatureFlagsInit } from './hooks/useFeatureFlagsInit';
import { usePendoLifecycle } from './hooks/usePendoLifecycle';
import { useRouteAfterRedirect } from './hooks/useRouteAfterRedirect';
import { useStaffingChatHasOpened } from './hooks/useStaffingChatHasOpened';

const StaffingChatPopup = lazyWithReload(
  () => import('components/staffing-chat/StaffingChatPopup')
);
const FinishInfoPage = lazyWithReload(
  () => import('pages/login/FinishInfoPage')
);

export const Layout = () => {
  const location = useLocation();
  const navigate = useAppNavigate();
  const { isAuthenticated, isAccountReady, member } = useSessionVerification();
  const needsSupportSelection = useNeedsSupportSelection();
  const { userId, orgId, email: userEmail } = useSessionIdentity();
  const isMobile = useIsMobile();

  const chatHasOpened = useStaffingChatHasOpened();
  const whatsNewEnabled = useFeatureFlagsStore((state) =>
    state.isFeatureEnabled(FeatureFlag.whats_new)
  );

  const showFinishInfoModal =
    isAuthenticated &&
    !isAccountReady &&
    !needsSupportSelection &&
    isProtectedPagePath(location.pathname);

  const isBullhornEmbedMode = useBullhornEmbedMode(location.pathname);
  const shouldApplyBullhornZoom = shouldApplyBullhornEmbedZoom(
    location.pathname
  );

  // Session verification is started by App.tsx at module scope, before this
  // mounts. Call order below is load-bearing: the cross-tab storage listener is
  // installed before the redirect hook reads localStorage.
  usePendoLifecycle(isAccountReady, member, location.pathname, location.search);

  useFeatureFlagsInit(isAccountReady, userId, orgId);

  useCrossTabAuthSync(navigate);

  useRouteAfterRedirect(location, navigate, isAccountReady);

  useDatadogRumUser(isAccountReady ? userId : null, userEmail);

  if (showFinishInfoModal) {
    return (
      <React.Suspense
        fallback={
          <Center h='100vh'>
            <Loader size='sm' />
          </Center>
        }
      >
        <FinishInfoPage opened />
      </React.Suspense>
    );
  }

  const hideNav = shouldHideSideNavBar(location.pathname, isAccountReady);

  const outlet = (
    <React.Suspense
      fallback={
        <Center h='100vh' w='100%'>
          <Loader size='sm' />
        </Center>
      }
    >
      <Outlet />
    </React.Suspense>
  );

  const mainContent = hideNav ? (
    outlet
  ) : (
    <div
      className={classNames(
        appShell.mainColumn,
        shouldApplyBullhornZoom && appShell.mainColumnBullhornZoom
      )}
    >
      <NoPhoneNumbersBanner />
      <SmsRegistrationDeadlineBanner />
      <div className={appShell.outletRow}>{outlet}</div>
    </div>
  );

  return (
    <>
      <div
        style={{
          height: '100%',
          minHeight: '100%',
          maxHeight: '100vh',
          display: shouldUseContentsDisplay(location.pathname)
            ? 'contents'
            : 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          ...(shouldApplyBullhornZoom && { zoom: 0.85 }),
        }}
      >
        {!hideNav && !isBullhornEmbedMode && <SideNavBar />}
        {mainContent}
      </div>
      {isAccountReady && <BullhornEmbedNav />}
      {isAccountReady &&
        !hideNav &&
        !isBullhornEmbedMode &&
        whatsNewEnabled && (
          <FeatureErrorBoundary feature='whats_new_popup' variant='silent'>
            <WhatsNewPopup />
          </FeatureErrorBoundary>
        )}
      {!hideNav && !isBullhornEmbedMode && chatHasOpened && (
        <FeatureErrorBoundary feature='staffing_chat_popup' variant='silent'>
          <React.Suspense fallback={null}>
            <StaffingChatPopup />
          </React.Suspense>
        </FeatureErrorBoundary>
      )}
    </>
  );
};
